import { logger } from "@/shared/lib/logger";
import React, { Component, ErrorInfo, ReactNode } from "react";
import { isChunkLoadError, CHUNK_RELOAD_KEY } from "@/shared/utils/chunkLoadError";

interface Props {
  children: ReactNode;
  fallback: React.ComponentType<{ error: string; onRetry?: () => void }>;
}

interface State {
  hasError: boolean;
  error: string | null;
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
    isChunkError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error: error.message,
      isChunkError: isChunkLoadError(error),
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error("ErrorBoundary caught an error:", error, errorInfo);

    // If this is a chunk-load failure caused by a new deployment, auto-reload
    // once to fetch the latest index.html and re-resolve all chunk URLs.
    // Guard against infinite reload loops: only reload if we haven't already
    // attempted a reload in this session (mirrors the guard in main.tsx).
    if (isChunkLoadError(error)) {
      const alreadyAttempted = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      if (!alreadyAttempted) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.reload();
      }
      // If already attempted: fall through — render() will show null,
      // keeping the page blank until the user manually refreshes.
    }
  }

  public override render() {
    if (this.state.hasError) {
      // For chunk errors the page is already reloading (triggered in
      // componentDidCatch); render nothing while the reload is in flight.
      if (this.state.isChunkError) {
        return null;
      }

      const FallbackComponent = this.props.fallback;
      return (
        <FallbackComponent
          error={this.state.error || "Something went wrong"}
          onRetry={() => this.setState({ hasError: false, error: null, isChunkError: false })}
        />
      );
    }

    return this.props.children;
  }
}
